import { useState } from "react";
import { PageShell } from "../../components/service/PageShell";
import { ServiceHero, CtaPrimary, CtaGhost, FeatureGrid, StepsList, CompareTable, FaqBlock, Section, Overline, SectionTitle, Accent, Reveal } from "../../components/service/Blocks";
import { LeadForm } from "../../components/LeadForm";
import { waLink } from "../../data/site";
import { Signal, PiggyBank, Phone, MessageCircle, Ban, Gift, Cpu, MapPin, Check, Gift as GiftIcon } from "lucide-react";

const WA_BASE = "Vim pelo site. Quero saber dos planos de celular iGreen Telecom.";
const VOUCHER_MSG = `${WA_BASE}\nQuero o voucher de 30 dias grátis no plano Start, com a portabilidade do meu número.`;

const PLANS = [
  { name: "Start", desc: "Economia sem abrir mão da conexão.", gbPort: 11, gbNew: 6, detPort: "4 GB + 2 GB de bônus + 5 GB da portabilidade", detNew: "4 GB + 2 GB de bônus de ativação", pricePort: "54,90", priceNew: "59,90", acum: "8 GB", badge: "30 dias grátis na portabilidade" },
  { name: "Mega", desc: "Mais liberdade para navegar e compartilhar.", gbPort: 15, gbNew: 10, detPort: "7 GB + 3 GB de bônus + 5 GB da portabilidade", detNew: "7 GB + 3 GB de bônus de ativação", pricePort: "59,90", priceNew: "64,90", acum: "14 GB" },
  { name: "Giga", desc: "Para quem usa apps, redes sociais e vídeos todo dia.", gbPort: 20, gbNew: 15, detPort: "11 GB + 4 GB de bônus + 5 GB da portabilidade", detNew: "11 GB + 4 GB de bônus de ativação", pricePort: "69,90", priceNew: "74,90", acum: "22 GB" },
  { name: "Ultra", desc: "Para uma rotina conectada, com mais vídeo e streaming.", gbPort: 28, gbNew: 23, detPort: "19 GB + 4 GB de bônus + 5 GB da portabilidade", detNew: "19 GB + 4 GB de bônus de ativação", pricePort: "79,90", priceNew: "84,90", acum: "38 GB" },
  { name: "Infinity", desc: "O plano mais completo da iGreen Telecom.", gbPort: 50, gbNew: 45, detPort: "41 GB + 4 GB de bônus + 5 GB da portabilidade", detNew: "41 GB + 4 GB de bônus de ativação", pricePort: "99,90", priceNew: "104,90", acum: "82 GB" },
];

const FAQ = [
  { q: "Como ganho os 30 dias grátis?", a: "Quem faz a portabilidade no plano Start ganha um voucher de 30 dias grátis. Me chama no WhatsApp que eu te passo o código. É um voucher por número e vale só na portabilidade do Start." },
  { q: "Tem fidelidade ou multa?", a: "Não. Você pode trocar de plano ou sair quando quiser, sem multa." },
  { q: "Como funciona a internet que acumula?", a: "A internet que você não usa no mês passa para o mês seguinte, até o dobro da franquia principal do plano (no Start, até 8 GB). Se o pagamento atrasar, o acumulado é perdido." },
  { q: "O WhatsApp gasta a minha internet?", a: "Não. Mensagens, fotos e áudios no WhatsApp são ilimitados e não descontam da sua franquia." },
  { q: "Vou ficar sem sinal na portabilidade?", a: "Não. A troca leva cerca de 3 dias úteis e o seu número continua funcionando até a linha nova assumir." },
  { q: "Meu celular aceita eSIM?", a: "A maioria dos aparelhos recentes aceita, e o eSIM ativa na hora. Se o seu não aceitar, o chip físico chega pelo correio (frete de R$ 19,90)." },
  { q: "O iGreen Club é pago à parte?", a: "Não. Todo cliente iGreen Telecom tem o iGreen Club grátis, com descontos e cashback em milhares de parceiros pelo app." },
  { q: "Tenho mais de uma linha. Tem desconto?", a: "Sim. No Plano Família, cada linha a partir da 2ª no mesmo CPF ou CNPJ tem R$ 10 de desconto por mês." },
];

const PlanCard = ({ plan, portability, index }) => {
  const price = portability ? plan.pricePort : plan.priceNew;
  const gb = portability ? plan.gbPort : plan.gbNew;
  const det = portability ? plan.detPort : plan.detNew;
  const modeMsg = portability
    ? `Quero o Plano ${plan.name} com portabilidade (R$ ${plan.pricePort}/mês).`
    : `Quero o Plano ${plan.name} com número novo (R$ ${plan.priceNew}/mês).`;
  return (
    <Reveal delay={index * 0.06} className="snap-start shrink-0 w-[300px]">
      <div
        data-testid={`telecom-plan-${plan.name.toLowerCase()}`}
        className={`h-full rounded-2xl border p-7 transition-[border-color,transform] duration-300 hover:-translate-y-1.5 flex flex-col ${
          portability ? "border-[#00E676]/45 bg-[#0B261E]/80 shadow-[0_0_30px_rgba(0,230,118,0.12)]" : "border-white/10 bg-[#0B261E]/50"
        }`}
      >
        {plan.badge && portability && (
          <span className="mb-4 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#A3E635] text-[#04120E] text-[10px] font-bold px-3 py-1">
            <GiftIcon size={11} /> {plan.badge}
          </span>
        )}
        <h3 className="font-heading text-xl font-extrabold text-white">Plano {plan.name}</h3>
        <p className="mt-1 text-xs text-emerald-100/50">{plan.desc}</p>
        <p className="mt-5 font-heading text-4xl font-extrabold text-[#00E676]">{gb}<span className="text-base text-emerald-100/50 font-semibold"> GB</span></p>
        <p className="mt-1 text-[11px] text-emerald-100/45">{det}</p>
        <p className="mt-5 font-heading text-2xl font-extrabold text-white">
          R$ {price}<span className="text-xs text-emerald-100/45 font-normal">/mês</span>
        </p>
        <ul className="mt-5 space-y-2 text-xs text-emerald-100/60 flex-1">
          {["Ligações ilimitadas", "WhatsApp ilimitado", `Internet que acumula (até ${plan.acum})`, "5G nas áreas com cobertura", "Sem fidelidade", "iGreen Club grátis"].map((f) => (
            <li key={f} className="flex gap-2 items-start"><Check size={13} className="text-[#00E676] mt-0.5 shrink-0" />{f}</li>
          ))}
        </ul>
        <a
          href={waLink(`${WA_BASE}\n${modeMsg}`)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`telecom-plan-${plan.name.toLowerCase()}-cta`}
          className="mt-6 inline-flex justify-center rounded-full bg-[#00E676] hover:bg-[#00C853] text-[#04120E] text-sm font-bold px-5 py-3 transition-colors duration-300"
        >
          {portability ? "Fazer portabilidade" : "Contratar número novo"}
        </a>
      </div>
    </Reveal>
  );
};

export default function Telecom() {
  const [portability, setPortability] = useState(true);
  return (
    <PageShell testid="page-telecom">
      <ServiceHero
        testid="telecom-hero"
        overline="iGreen Telecom"
        title={["Celular 5G com internet", "que acumula."]}
        sub="Ligações e WhatsApp ilimitados, 5G, internet que passa para o mês seguinte e o iGreen Club grátis, sem fidelidade. Traga seu número e ganhe +5 GB e um preço menor."
        bullets={["11 GB por R$ 54,90", "+5 GB na portabilidade", "Sem fidelidade", "iGreen Club grátis"]}
        primary={<CtaPrimary href="#planos" external={false} testid="telecom-portability-cta">Fazer portabilidade</CtaPrimary>}
        secondary={<CtaGhost href="#planos" external={false} testid="telecom-new-number-cta">Adquirir número novo</CtaGhost>}
      />

      <Section className="bg-[#061A14]" testid="telecom-voucher">
        <Reveal>
          <div className="rounded-3xl border border-[#A3E635]/30 bg-[#0B261E]/60 backdrop-blur-md p-8 sm:p-12">
            <p className="text-xs font-mono2 uppercase tracking-[0.25em] text-[#A3E635] mb-4">Oferta só na portabilidade</p>
            <SectionTitle className="max-w-3xl">Faça sua portabilidade agora e ganhe <Accent>30 dias grátis</Accent> no plano Start.</SectionTitle>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/60 leading-relaxed">
              Traga o seu número para a iGreen Telecom e use o plano Start por 30 dias sem pagar nada: 11 GB de internet,
              ligações e WhatsApp ilimitados e o iGreen Club grátis. Eu te passo o código do voucher pelo WhatsApp.
            </p>
            <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-xs text-emerald-100/60">
              {["11 GB de internet", "Ligações ilimitadas", "WhatsApp ilimitado", "iGreen Club grátis"].map((t) => (
                <span key={t} className="inline-flex items-center gap-1.5"><Check size={13} className="text-[#A3E635]" /> {t}</span>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap gap-4">
              <CtaPrimary href={waLink(VOUCHER_MSG)} testid="telecom-voucher-cta">Quero meu voucher de 30 dias</CtaPrimary>
              <CtaGhost href="#planos" external={false} testid="telecom-see-start-cta">Ver o plano Start</CtaGhost>
            </div>
            <p className="mt-5 text-[11px] text-emerald-100/40">
              Voucher válido só para portabilidade no plano Start, um por número. Os primeiros 30 dias são grátis; depois, R$ 54,90 por mês, sem fidelidade.
            </p>
          </div>
        </Reveal>
      </Section>

      <Section testid="telecom-todos-planos">
        <Reveal>
          <Overline>Em todos os planos</Overline>
          <SectionTitle>Tudo isso vem <Accent>em qualquer plano</Accent>.</SectionTitle>
          <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55">
            Você escolhe só quanto de internet quer. O resto já está incluso, do Start ao Infinity.
          </p>
        </Reveal>
        <div className="mt-12">
          <FeatureGrid
            testid="telecom-features"
            items={[
              { icon: Signal, title: "5G de alta velocidade", text: "Navegue, assista e faça videochamadas com a velocidade do 5G, nas áreas com cobertura." },
              { icon: PiggyBank, title: "Internet que acumula", text: "A internet que sobra no mês passa para o mês seguinte, em vez de sumir no fim do ciclo." },
              { icon: Phone, title: "Ligações ilimitadas", text: "Fale à vontade com qualquer operadora do Brasil, sem contar minuto." },
              { icon: MessageCircle, title: "WhatsApp ilimitado", text: "Mensagens, fotos e áudios no WhatsApp não gastam a sua internet." },
              { icon: Ban, title: "Sem fidelidade", text: "Troque de plano ou saia quando quiser, sem multa e sem burocracia." },
              { icon: Gift, title: "iGreen Club grátis", text: "Descontos e cashback em farmácia, cinema, delivery e milhares de parceiros, direto no app." },
              { icon: Cpu, title: "eSIM ou chip em casa", text: "Ative o eSIM na hora no celular compatível ou receba o chip físico no seu endereço." },
              { icon: MapPin, title: "Cobertura nacional", text: "Use o seu número em todo o Brasil, com o mesmo plano e o mesmo preço." },
            ]}
          />
        </div>
      </Section>

      <Section className="bg-[#061A14]" testid="telecom-planos">
        <div id="planos">
          <Reveal>
            <Overline>Planos</Overline>
            <SectionTitle>Escolha o seu plano e ganhe <Accent>+5 GB na portabilidade</Accent>.</SectionTitle>
            <p className="mt-4 max-w-2xl text-sm sm:text-base text-emerald-100/55">
              Trazendo o seu número, o plano fica mais barato e vem com 5 GB de bônus.
            </p>
            <div className="mt-8 inline-flex rounded-full border border-white/12 bg-[#04120E]/70 p-1.5" data-testid="telecom-mode-toggle">
              <button
                onClick={() => setPortability(true)}
                data-testid="telecom-mode-portability"
                className={`rounded-full text-xs font-bold px-5 py-2.5 transition-colors duration-300 ${portability ? "bg-[#00E676] text-[#04120E]" : "text-emerald-100/60"}`}
              >
                Com portabilidade
              </button>
              <button
                onClick={() => setPortability(false)}
                data-testid="telecom-mode-new"
                className={`rounded-full text-xs font-bold px-5 py-2.5 transition-colors duration-300 ${!portability ? "bg-[#00E676] text-[#04120E]" : "text-emerald-100/60"}`}
              >
                Número novo
              </button>
            </div>
          </Reveal>
          <div className="mt-10 flex gap-5 overflow-x-auto pb-4 snap-x" data-testid="telecom-plans-row">
            {PLANS.map((p, i) => <PlanCard key={p.name} plan={p} portability={portability} index={i} />)}
          </div>
          <p className="text-[11px] text-emerald-100/40">Arraste para o lado para ver todos os planos →</p>
          <p className="mt-4 text-[11px] text-emerald-100/40 leading-relaxed max-w-3xl">
            Valores mensais divulgados pela iGreen Telecom. A internet acumula até o dobro da franquia principal do plano
            (os bônus não aumentam esse limite) e o acumulado é perdido se o pagamento atrasar. Chip físico tem frete de
            R$ 19,90; o eSIM é ativado direto no celular compatível. No Plano Família, cada linha a partir da 2ª no mesmo CPF ou CNPJ tem R$ 10 de desconto por mês.
          </p>
        </div>
      </Section>

      <Section testid="telecom-compare">
        <Reveal>
          <Overline>Compare</Overline>
          <SectionTitle>iGreen Telecom <Accent>x planos tradicionais</Accent>.</SectionTitle>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-12">
            <CompareTable
              testid="telecom-compare-table"
              leftName="iGreen Telecom"
              rightName="Planos tradicionais"
              rows={[
                ["Internet que sobra no mês", "Passa para o mês seguinte", "Some no fim do ciclo"],
                ["Fidelidade", "Nenhuma", "Comum em planos com desconto"],
                ["Multa para sair", "Não tem", "Pode ter"],
                ["WhatsApp", "Ilimitado, sem gastar internet", "Depende do plano"],
                ["Contratação", "100% digital, eSIM ou chip em casa", "Loja ou atendimento"],
                ["Bônus na portabilidade", "+5 GB e preço menor", "Nem sempre"],
                ["Clube de descontos", "iGreen Club grátis", "Raramente"],
              ]}
              note="Comparação com condições comuns no mercado de telefonia móvel; cada operadora tem as suas regras."
            />
          </div>
        </Reveal>
      </Section>

      <Section className="bg-[#061A14]" testid="telecom-passos">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <Overline>Passo a passo</Overline>
            <SectionTitle>Como trocar de <Accent>operadora</Accent></SectionTitle>
          </Reveal>
          <StepsList
            testid="telecom-steps"
            steps={[
              { n: "01", title: "Você escolhe o plano", text: "Do Start ao Infinity. Na dúvida, me diga quanto de internet você usa e eu indico." },
              { n: "02", title: "Escolhe eSIM ou chip", text: "eSIM ativa direto no aparelho compatível; o chip físico chega pelo correio." },
              { n: "03", title: "Faz a portabilidade", text: "Seu número vem junto, em cerca de 3 dias úteis, sem ficar sem sinal." },
              { n: "04", title: "Usa e acumula", text: "A internet que sobra passa para o mês seguinte." },
            ]}
          />
        </div>
      </Section>

      <FaqBlock testid="telecom-faq" title={<>Perguntas sobre <Accent>iGreen Telecom</Accent></>} items={FAQ} />

      <LeadForm
        overline="Vamos conversar?"
        title={<>Me conta o seu caso e eu te respondo <Accent>no WhatsApp</Accent>.</>}
        defaultInterest="Plano de celular"
      />
    </PageShell>
  );
}
